import React from "react";
import styled from "styled-components";
import { Check, Minus } from "react-feather";
import { rawColors } from "../../../design-system/tokens/colors";
import { borderRadius } from "../../../design-system/tokens/borders";
import { duration, easing } from "../../../design-system/tokens/transitions";

export interface CheckboxProps {
  checked?: boolean;
  /** Renders the partially-checked dash; takes visual precedence over `checked`. */
  indeterminate?: boolean;
  disabled?: boolean;
  onChange?: (next: boolean) => void;
  ariaLabel?: string;
  className?: string;
}

const Wrapper = styled.label<{ $disabled: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
`;

const HiddenInput = styled.input`
  position: absolute;
  inset: 0;
  opacity: 0;
  margin: 0;
  cursor: inherit;
`;

const Box = styled.span<{ $on: boolean; $disabled: boolean }>`
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.sm};
  background-color: ${({ $on }) =>
    $on ? rawColors.primary[500] : rawColors.structural.white};
  border: 1px solid
    ${({ $on }) => ($on ? rawColors.primary[500] : rawColors.stroke[300])};
  color: ${rawColors.text.white};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition:
    background-color ${duration.fast} ${easing.smooth},
    border-color ${duration.fast} ${easing.smooth};
`;

export const Checkbox: React.FC<CheckboxProps> = ({
  checked = false,
  indeterminate = false,
  disabled = false,
  onChange,
  ariaLabel,
  className,
}) => {
  const on = checked || indeterminate;
  return (
    <Wrapper $disabled={disabled} className={className}>
      <HiddenInput
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        aria-label={ariaLabel}
        aria-checked={indeterminate ? "mixed" : checked}
      />
      <Box $on={on} $disabled={disabled}>
        {indeterminate ? (
          <Minus size={12} strokeWidth={3} aria-hidden />
        ) : checked ? (
          <Check size={12} strokeWidth={3} aria-hidden />
        ) : null}
      </Box>
    </Wrapper>
  );
};

export default Checkbox;
